// TanStack Query wrappers for the Loyalty feature.
//
// Tenant-scoped cache keys (`['loyalty', tenantId, ...]`) mirror the pattern
// in features/customers/use-customer-query.ts so invalidation is consistent.

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/use-auth-store';
import { loyaltyApi } from './api';
import type { LoyaltyConfig, LoyaltyStats, LoyaltyTransactionRow, LoyaltyTxType, TopLoyaltyCustomerRow } from './types';

function useTenantId(): string {
  return useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');
}

export function useLoyaltyConfig(): UseQueryResult<LoyaltyConfig> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['loyalty', tenantId, 'config'],
    queryFn: () => loyaltyApi.getConfig(),
    staleTime: 60_000,
  });
}

export function useLoyaltyStats(): UseQueryResult<LoyaltyStats> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['loyalty', tenantId, 'stats'],
    queryFn: () => loyaltyApi.getStats(),
    staleTime: 60_000,
  });
}

export function useTopLoyaltyCustomers(limit = 10): UseQueryResult<TopLoyaltyCustomerRow[]> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['loyalty', tenantId, 'top-customers', limit],
    queryFn: () => loyaltyApi.getTopCustomers(limit),
    staleTime: 60_000,
  });
}

export function useLoyaltyTransactions(
  params: { type?: LoyaltyTxType; limit?: number } = {},
): UseQueryResult<LoyaltyTransactionRow[]> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['loyalty', tenantId, 'transactions', params.type ?? 'ALL', params.limit ?? 50],
    queryFn: () => loyaltyApi.listTransactions(params),
    staleTime: 30_000,
  });
}

export function useUpdateLoyaltyConfig() {
  const qc = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: (input: LoyaltyConfig) => loyaltyApi.updateConfig(input),
    onSuccess: (saved) => {
      qc.setQueryData(['loyalty', tenantId, 'config'], saved);
      qc.invalidateQueries({ queryKey: ['loyalty', tenantId, 'stats'] });
    },
  });
}
